import React, { useState, useEffect, useMemo } from 'react';
import { 
    DollarSign, TrendingUp, Megaphone, Zap, BrainCircuit, 
    FileText, Target, ArrowUpRight, Activity, PieChart as PieIcon,
    BarChart3, Calendar, Layers, Sliders
} from 'lucide-react';
import { 
    ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, ComposedChart, Bar, Line
} from 'recharts';

// Shared UI
import { 
    AppCard, AppButton, BlackboxLoaderIcon 
} from '../../components/SharedUI';

// Services
import { generatePerformanceSummaryLLM } from '../../services/aiService';

// --- STYLED COMPONENTS ---

const KPICard = ({ label, value, subtext, icon: Icon, color = "emerald", trend }) => {
    const colors = {
        emerald: "text-emerald-400 bg-emerald-900/20 border-emerald-500/30",
        blue: "text-blue-400 bg-blue-900/20 border-blue-500/30",
        purple: "text-purple-400 bg-purple-900/20 border-purple-500/30",
        amber: "text-amber-400 bg-amber-900/20 border-amber-500/30",
    };

    return (
        <div className={`p-5 rounded-xl border bg-zinc-900/50 backdrop-blur-sm flex flex-col justify-between h-full ${colors[color] ? colors[color].replace('text-', 'border-') : 'border-zinc-800'}`}>
            <div className="flex justify-between items-start mb-2">
                <div className={`p-2 rounded-lg ${colors[color]}`}>
                    <Icon size={20} />
                </div>
                {trend && (
                    <span className="flex items-center gap-1 text-[10px] font-bold bg-zinc-900/50 px-2 py-1 rounded-full border border-white/5">
                        <ArrowUpRight size={10} /> {trend}
                    </span>
                )}
            </div>
            <div>
                <p className="text-3xl font-bold text-white font-mono tracking-tight mb-1">{value}</p>
                <p className="text-xs text-zinc-400 uppercase tracking-widest font-bold">{label}</p>
                {subtext && <p className="text-[10px] text-zinc-500 mt-2">{subtext}</p>}
            </div>
        </div>
    );
};

const ROISimulator = ({ revenueBase, budgetBase }) => {
    const [multiplier, setMultiplier] = useState(1.0); // 1.0 = Base Case
    
    // Simulate market conditions
    const simulatedRevenue = revenueBase * multiplier;
    const netProfit = simulatedRevenue - budgetBase;
    const roi = budgetBase > 0 ? ((netProfit / budgetBase) * 100).toFixed(1) : 0;

    const getConditionLabel = (val) => {
        if (val < 0.8) return { label: "Recession / Low Buzz", color: "text-red-400" };
        if (val < 1.0) return { label: "Conservative", color: "text-amber-400" };
        if (val === 1.0) return { label: "Base Projection", color: "text-blue-400" };
        if (val < 1.3) return { label: "Strong Word of Mouth", color: "text-emerald-400" };
        return { label: "Viral Breakout", color: "text-purple-400" };
    };

    const condition = getConditionLabel(multiplier);

    return (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Sliders size={18} className="text-blue-500" />
                    Market Scenario Simulator
                </h3>
                <span className={`text-xs font-bold uppercase px-3 py-1 rounded-full bg-zinc-950 border border-zinc-800 ${condition.color}`}>
                    {condition.label}
                </span>
            </div>

            <div className="mb-8 px-2">
                <input 
                    type="range" 
                    min="0.5" 
                    max="2.0" 
                    step="0.1" 
                    value={multiplier}
                    onChange={(e) => setMultiplier(parseFloat(e.target.value))}
                    className="w-full accent-blue-500 h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-zinc-500 font-bold uppercase mt-3">
                    <span>Bear Case (-50%)</span>
                    <span>Base Model</span>
                    <span>Bull Case (+100%)</span>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-zinc-950 rounded-xl border border-zinc-800">
                    <p className="text-xs text-zinc-500 uppercase mb-1">Adj. Revenue</p>
                    <p className="text-xl font-mono text-white font-bold">
                        ${(simulatedRevenue / 1000000).toFixed(1)}M
                    </p>
                </div>
                <div className="p-4 bg-zinc-950 rounded-xl border border-zinc-800">
                    <p className="text-xs text-zinc-500 uppercase mb-1">Net Profit</p>
                    <p className={`text-xl font-mono font-bold ${netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        ${(netProfit / 1000000).toFixed(1)}M
                    </p>
                </div>
                <div className="p-4 bg-zinc-950 rounded-xl border border-zinc-800">
                    <p className="text-xs text-zinc-500 uppercase mb-1">Proj. ROI</p>
                    <p className={`text-xl font-mono font-bold ${roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {roi}%
                    </p>
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---

const AnalyticsTab = ({ filmData, setNotification, saveFilmData, credits, deductCredit }) => {
    const [aiSummary, setAiSummary] = useState(filmData.performance_summary || null);
    const [loading, setLoading] = useState(false);

    // --- DATA AGGREGATION ---
    const budgetTotal = useMemo(() => {
        if (!filmData.budget_plan?.budget_allocation_table) return 0;
        return filmData.budget_plan.budget_allocation_table.reduce((sum, item) => sum + (item.dollar_amount || 0), 0);
    }, [filmData.budget_plan]);

    const revenueForecast = useMemo(() => {
        if (!filmData.revenue_forecast?.data) return 0;
        const d = filmData.revenue_forecast.data;
        // Logic to extract a single "Total" number based on type
        if (filmData.revenue_forecast.type === 'theatrical') {
            return (d.theatrical_release_projections?.domestic_box_office_forecast || 0) + 
                   (d.theatrical_release_projections?.international_box_office_forecast || 0);
        } else {
            // Streaming total logic
            return 5000000; // Placeholder/Fallback if streaming structure varies
        }
    }, [filmData.revenue_forecast]);

    const campaignCount = useMemo(() => {
        // Assuming campaigns are stored elsewhere or we count a placeholder. 
        // If campaigns array is passed as prop, use it. For now, defaulting or checking filmData structure.
        return 0; // Modify if campaigns are available in filmData
    }, [filmData]);

    const budgetAllocationData = useMemo(() => {
        return filmData.budget_plan?.budget_allocation_table?.map(item => ({
            name: item.bucket,
            value: item.dollar_amount
        })) || [];
    }, [filmData.budget_plan]);

    const formatCurrency = (value) => {
        if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
        return `$${value}`;
    };

    // --- AI SUMMARY GENERATION ---
    const handleGenerateSummary = async () => {
        if (credits < 2) {
            setNotification({ type: 'error', message: 'Insufficient credits.' });
            return;
        }
        if (!budgetTotal && !revenueForecast) {
            setNotification({ type: 'error', message: 'Financial data missing. Run Budget & Revenue tools first.' });
            return;
        }

        setLoading(true);
        try {
            // Simulated AI call structure
            const summary = await generatePerformanceSummaryLLM(filmData); 
            setAiSummary(summary);
            await saveFilmData({ ...filmData, performance_summary: summary });
            await deductCredit(2);
            setNotification({ type: 'success', message: 'Executive summary generated.' });
        } catch (error) {
            setNotification({ type: 'error', message: 'Summary generation failed.' });
        } finally {
            setLoading(false);
        }
    };

    // Chart Data Construction
    const trajectoryData = [
        { name: 'Pre-Prod', spend: budgetTotal * 0.2, revenue: 0 },
        { name: 'Prod', spend: budgetTotal * 0.6, revenue: 0 },
        { name: 'Post', spend: budgetTotal * 0.8, revenue: 0 },
        { name: 'Release', spend: budgetTotal, revenue: revenueForecast * 0.3 },
        { name: 'Month 1', spend: budgetTotal * 1.1, revenue: revenueForecast * 0.6 },
        { name: 'Month 3', spend: budgetTotal * 1.15, revenue: revenueForecast * 0.8 },
        { name: 'Year 1', spend: budgetTotal * 1.2, revenue: revenueForecast },
    ];

    return (
        <div className="h-full flex flex-col space-y-8 pb-20 animate-in fade-in duration-500">
            
            {/* --- HEADER: EXECUTIVE SUMMARY --- */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white font-poppins tracking-tight">Executive Dashboard</h2>
                    <p className="text-sm text-zinc-400">Project Performance & ROI Outlook</p>
                </div>
                <AppButton 
                    onClick={handleGenerateSummary} 
                    disabled={loading} 
                    icon={BrainCircuit}
                    className="bg-purple-600 hover:bg-purple-500 shadow-lg shadow-purple-900/20"
                >
                    {loading ? 'Analyzing...' : 'Generate AI Brief'}
                </AppButton>
            </div>

            {/* AI BRIEF BLOCK */}
            {aiSummary && (
                <div className="bg-zinc-900/50 border-l-4 border-purple-500 p-6 rounded-r-xl relative">
                    <h3 className="text-sm font-bold text-purple-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <Zap size={14} /> Strategic Insight
                    </h3>
                    <p className="text-zinc-200 leading-relaxed text-sm md:text-base">
                        "{aiSummary}"
                    </p>
                </div>
            )}

            {/* --- KPI GRID --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard 
                    label="Total Budget" 
                    value={formatCurrency(budgetTotal)} 
                    subtext="Allocated Capital"
                    icon={DollarSign} 
                    color="amber"
                />
                <KPICard 
                    label="Projected Revenue" 
                    value={formatCurrency(revenueForecast)} 
                    subtext="Base Model Forecast"
                    icon={TrendingUp} 
                    color="emerald"
                    trend={revenueForecast > budgetTotal ? "+ Profitable" : "High Risk"}
                />
                <KPICard 
                    label="ROI Multiple" 
                    value={budgetTotal > 0 ? `${(revenueForecast / budgetTotal).toFixed(1)}x` : "N/A"} 
                    subtext="Est. Return on Spend"
                    icon={Target} 
                    color="blue"
                />
                <KPICard 
                    label="Active Assets" 
                    value={filmData.custom_graphics?.length || 0} 
                    subtext="Campaign Creative"
                    icon={Layers} 
                    color="purple"
                />
            </div>

            {/* --- VISUALIZATION ROW --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* 1. PROFITABILITY TRAJECTORY (2/3 width) */}
                <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-6 flex flex-col">
                    <div className="mb-6 flex justify-between items-center">
                        <h3 className="font-bold text-white text-lg flex items-center gap-2">
                            <Activity size={18} className="text-blue-500" />
                            Profitability Trajectory
                        </h3>
                        <div className="flex gap-4 text-xs font-bold uppercase tracking-wider">
                            <span className="flex items-center gap-1 text-zinc-400"><div className="w-2 h-2 rounded-full bg-red-500/50"/> Cumulative Spend</span>
                            <span className="flex items-center gap-1 text-zinc-400"><div className="w-2 h-2 rounded-full bg-emerald-500"/> Revenue</span>
                        </div>
                    </div>
                    
                    <div className="h-64 w-full">
                        {budgetTotal > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={trajectoryData}>
                                    <defs>
                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                                    <XAxis dataKey="name" stroke="#52525b" fontSize={10} tickLine={false} axisLine={false} />
                                    <YAxis stroke="#52525b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(val) => `$${val/1000000}M`} />
                                    <Tooltip 
    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
    itemStyle={{ color: '#e4e4e7', fontSize: '12px' }} // <--- ADDED COLOR
    labelStyle={{ color: '#a1a1aa' }} // <--- ADDED COLOR
    formatter={(value) => formatCurrency(value)}
/>
                                    <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" />
                                    <Area type="monotone" dataKey="spend" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" fillOpacity={1} fill="url(#colorSpend)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex items-center justify-center text-zinc-600 text-sm">
                                No financial data points.
                            </div>
                        )}
                    </div>
                </div>

                {/* 2. SPEND MIX (1/3 width) */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 flex flex-col">
                    <h3 className="font-bold text-white text-lg mb-6 flex items-center gap-2">
                        <PieIcon size={18} className="text-amber-500" />
                        Allocation Mix
                    </h3>
                    <div className="flex-1 min-h-[200px]">
                        {budgetAllocationData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie 
                                        data={budgetAllocationData} 
                                        innerRadius={60} 
                                        outerRadius={80} 
                                        paddingAngle={5} 
                                        dataKey="value"
                                    >
                                        {budgetAllocationData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'][index % 5]} stroke="rgba(0,0,0,0)" />
                                        ))}
                                    </Pie>
                                    <Tooltip 
    formatter={(value) => formatCurrency(value)}
    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
    itemStyle={{ color: '#e4e4e7', fontSize: '12px' }} // <--- ADDED COLOR
/>
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex items-center justify-center text-zinc-600 text-sm">
                                Budget not finalized.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* --- BOTTOM ROW: SIMULATION --- */}
            <ROISimulator revenueBase={revenueForecast} budgetBase={budgetTotal} />

        </div>
    );
};

export default AnalyticsTab;